/** The leave-settlement API as React Query hooks; `routers/leave_settlement.py` is the source of truth and this only mirrors its wire.
 *  POST is self-service; a decision signs every stage the caller holds by the leave chain, and the route lists those stages per row as `my_stages`. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'

import { api } from './api'
import { formatLeaveDays } from './leaveDays'
import { formatMoney } from './money'

//: The values `SETTLEMENT_TRIGGER_PATTERN` accepts, as a runtime array so a form can iterate it.
export const SETTLEMENT_TRIGGERS = ['Resignation', 'Maternity Leave', 'Other'] as const
export type SettlementTrigger = (typeof SETTLEMENT_TRIGGERS)[number]

//: `Resignation` and `Maternity Leave` need a `relevant_date`; the router now refuses both from the employee.
export const TRIGGERS_NEEDING_A_DATE: readonly SettlementTrigger[] = ['Resignation', 'Maternity Leave']

//: What an employee may raise; HR's close-out pays a Resignation and payroll's break payout a maternity leave, so the router refuses both.
export const REQUESTABLE_TRIGGERS: readonly SettlementTrigger[] = ['Other']

/** Contractors hold no annual leave to pay out; the router refuses them too. */
export function mayRequestLeavePayout(workType: string | null | undefined): boolean {
  return (workType ?? '').trim().toLowerCase() !== 'contractor'
}

export type SettlementStage = 'supervisor' | 'management' | 'finance'
type SettlementStatus = 'Pending' | 'Approved' | 'Rejected'
type SettlementVerdict = 'Approved' | 'Rejected'

export interface Settlement {
  id: string
  person_id: string
  trigger_type: string
  relevant_date: string | null
  requested_days: number
  reason: string
  //: Empty on a row whose chain has no supervisor stage.
  supervisor_approval: SettlementStatus | ''
  management_approval: SettlementStatus
  finance_approval: SettlementStatus
  status: SettlementStatus
  approved_date: string | null
  prev_al_days: number | null
  comment: string
  settled_at: string | null
  //: The priced pay, null until approved and for a caller who may not see pay.
  amount: string | null
  salary_run_id: string | null
  created_at: string
  my_stages: SettlementStage[]
}

interface SettlementCreateInput {
  trigger_type: SettlementTrigger
  relevant_date?: string | null
  requested_days: number
  reason?: string
}

interface SettlementDecisionInput {
  settlement_id: string
  verdict: SettlementVerdict
  comment?: string
}

/** `PayoutPreviewOut`: Decimals arrive as strings; the three `estimated_` fields are null when they cannot be priced. */
export interface PayoutPreview {
  balance: string | null
  reserve: number
  max_days: string | null
  pending_days: string
  requested_days: string | null
  after: string | null
  allowed: boolean | null
  refused: string | null
  estimated_day_value: string | null
  estimated_amount: string | null
  estimated_pay_date: string | null
}

/** `FullSettlementOut`: Decimals arrive as strings, and `days` is null with a `reason` when it cannot be worked out. */
export interface FullSettlement {
  on: string | null
  counted_to: string | null
  balance: string | null
  allocation: string | null
  worked_days: number
  year_work_days: number
  earned: string | null
  unearned: string | null
  days: string | null
  reason: string | null
}

const settlementKeys = {
  all: ['leave-settlements'] as const,
}

/** The figure to send, or null when it is not known: unknown is never shown or sent as a number. */
export function fullSettlementDays(preview: FullSettlement | undefined): number | null {
  if (!preview || preview.days == null) return null
  const days = Number(preview.days)
  return Number.isFinite(days) && days > 0 ? days : null
}

/** @see settlementTriggerLabel */
export function fullSettlementReason(reason: string | null, t: TFunction): string | null {
  switch (reason) {
    case 'no_leaving_date':
      return t('payroll.settlementFullNoLeavingDate')
    case 'no_break_start':
      return t('payroll.settlementFullNoBreakStart')
    case 'no_break_on_record':
      return t('payroll.settlementFullNoBreakOnRecord')
    case 'no_leave_info':
      return t('payroll.settlementFullNoLeaveInfo')
    case 'al_allocation_unknown':
      return t('payroll.settlementFullAllocationUnknown')
    case 'nothing_to_settle':
      return t('payroll.settlementFullNothingToSettle')
    default:
      return null
  }
}

/** What an Other pay-out of `days` would do; days are sent only once they are a positive number. */
export function usePayoutPreview(days: string, enabled = true) {
  const n = Number(days)
  const requested = Number.isFinite(n) && n > 0 ? n : null
  return useQuery({
    queryKey: [...settlementKeys.all, 'payout-preview', requested],
    queryFn: async () =>
      (
        await api.get<PayoutPreview>('/api/leave-settlements/payout-preview', {
          params: requested == null ? {} : { requested_days: requested },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

/** Every settlement the caller may see; the route decides which. */
export function useSettlements() {
  return useQuery({
    queryKey: settlementKeys.all,
    queryFn: async () => (await api.get<Settlement[]>('/api/leave-settlements')).data,
  })
}

/** Raise a settlement against the caller's own accrued leave. */
export function useCreateSettlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SettlementCreateInput) =>
      (await api.post<Settlement>('/api/leave-settlements', input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementKeys.all }),
  })
}

/** Sign every stage the caller holds on one settlement, once. */
export function useDecideSettlement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ settlement_id, ...payload }: SettlementDecisionInput) =>
      (await api.post<Settlement>(`/api/leave-settlements/${settlement_id}/decision`, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementKeys.all }),
  })
}

/** Whether to offer Approve/Reject: the route lists the caller's open stages, so no ability is re-derived here. */
export function canDecideSettlement(
  row: Pick<Settlement, 'person_id' | 'status' | 'my_stages'>,
  meId: string | undefined,
): boolean {
  return !!meId && row.person_id !== meId && row.status === 'Pending' && row.my_stages.length > 0
}

/** The pay-outs the caller may sign now, narrowed to one person when the list is. */
export function payoutsAwaitingMe(
  rows: readonly Settlement[] | undefined,
  meId: string | undefined,
  personId: string | null = null,
): Settlement[] {
  return (rows ?? []).filter((s) => canDecideSettlement(s, meId) && (personId === null || s.person_id === personId))
}

/** The count badge's text; null hides the icon, so nothing shows at zero. */
export function payoutBadgeText(count: number): string | null {
  if (count <= 0) return null
  return count > 99 ? '99+' : String(count)
}

/** The awaiting tab's number: the leave applications and the pay-outs awaiting me. */
export function awaitingTabCount(leaveWaiting: number, payoutsWaiting: number): number {
  return leaveWaiting + payoutsWaiting
}

/** The empty leave list's line; with pay-out rows above it, it must not say there are no requests. */
export function leaveEmptyKey(filtered: boolean, payoutsShown: number): string {
  if (payoutsShown > 0) return filtered ? 'leave.noLeaveUnderFilter' : 'leave.noLeave'
  return filtered ? 'leave.noneUnderFilter' : 'leave.none'
}

/** The stages of a row in signing order; the supervisor stage only where the row carries one. */
export function settlementStageRows(
  row: Pick<Settlement, 'supervisor_approval' | 'management_approval' | 'finance_approval'>,
): { stage: SettlementStage; approval: SettlementStatus }[] {
  const rows: { stage: SettlementStage; approval: SettlementStatus }[] = []
  if (row.supervisor_approval) rows.push({ stage: 'supervisor', approval: row.supervisor_approval })
  rows.push({ stage: 'management', approval: row.management_approval })
  rows.push({ stage: 'finance', approval: row.finance_approval })
  return rows
}

/** `trigger_type` -> its label. A `switch` over literal `t(...)` calls, not a template key: `i18n/keys.test.ts` only sees literal call sites, and `"Maternity Leave"` has a space. */
export function settlementTriggerLabel(trigger: string, t: TFunction): string {
  switch (trigger) {
    case 'Resignation':
      return t('payroll.settlementTriggerResignation')
    case 'Maternity Leave':
      return t('payroll.settlementTriggerMaternity')
    case 'Other':
      return t('payroll.settlementTriggerOther')
    default:
      return trigger
  }
}

/** @see settlementTriggerLabel */
export function settlementStageLabel(stage: SettlementStage, t: TFunction): string {
  switch (stage) {
    case 'supervisor':
      return t('payroll.settlementStageSupervisor')
    case 'management':
      return t('payroll.settlementStageManagement')
    default:
      return t('payroll.settlementStageFinance')
  }
}

/** @see settlementTriggerLabel */
export function settlementStatusLabel(status: SettlementStatus, t: TFunction): string {
  switch (status) {
    case 'Approved':
      return t('payroll.settlementStatusApproved')
    case 'Rejected':
      return t('payroll.settlementStatusRejected')
    default:
      return t('payroll.settlementStatusPending')
  }
}

/** Whether the form can be sent: only a requestable trigger, with a positive count of its own. */
export function settlementFormValid(
  trigger: SettlementTrigger,
  relevantDate: string,
  requestedDays: string,
): boolean {
  if (!REQUESTABLE_TRIGGERS.includes(trigger)) return false
  const days = Number(requestedDays)
  if (!Number.isFinite(days) || days <= 0) return false
  if (TRIGGERS_NEEDING_A_DATE.includes(trigger) && !relevantDate) return false
  return true
}

/** How the full figure was worked out, or null unless every number in the sentence is known. */
export function fullSettlementWorking(preview: FullSettlement | undefined, t: TFunction): string | null {
  if (!preview || preview.days == null || preview.allocation == null || preview.counted_to == null) return null
  return t('payroll.settlementFullWorking', {
    balance: Number(preview.balance),
    unearned: Number(preview.unearned),
    allocation: Number(preview.allocation),
    worked: preview.worked_days,
    yearDays: preview.year_work_days,
    countedTo: preview.counted_to,
    earned: Number(preview.earned),
  })
}

export interface PayoutSummary {
  balance: string | null
  pending: string | null
  amount: string | null
  payDay: string | null
  problem: string | null
}

function payoutRefusal(refused: string, t: TFunction): string {
  switch (refused) {
    case 'contractor':
      return t('payroll.settlementRefusedContractor')
    case 'closed_out':
      return t('payroll.settlementRefusedClosedOut')
    default:
      return t('payroll.settlementFullNoLeaveInfo')
  }
}

/** The pay-out dialog's sentences; each is null unless every figure in it is known, and pay is only ever an estimate. */
export function payoutSummary(preview: PayoutPreview | undefined, t: TFunction, language: string): PayoutSummary {
  const none: PayoutSummary = { balance: null, pending: null, amount: null, payDay: null, problem: null }
  if (!preview) return none
  if (preview.refused) return { ...none, problem: payoutRefusal(preview.refused, t) }
  const days = (value: string | number) => formatLeaveDays(value, language)
  const refused = preview.allowed === false
  let problem: string | null = null
  if (refused && preview.max_days != null) {
    problem = preview.reserve
      ? t('payroll.settlementPreviewOverMaxReserve', { max: days(preview.max_days), reserve: preview.reserve })
      : t('payroll.settlementPreviewOverMax', { max: days(preview.max_days) })
  }
  let balance: string | null = null
  if (preview.balance != null) {
    balance =
      preview.after != null
        ? t('payroll.settlementPreviewBalance', { before: days(preview.balance), after: days(preview.after) })
        : t('payroll.settlementPreviewBalanceNow', { balance: days(preview.balance) })
  }
  const pendingDays = Number(preview.pending_days)
  const pending =
    Number.isFinite(pendingDays) && pendingDays > 0
      ? t('payroll.settlementPreviewPending', { days: days(pendingDays) })
      : null
  const amount =
    !refused && preview.estimated_amount != null && preview.estimated_day_value != null && preview.requested_days != null
      ? t('payroll.settlementPreviewAmount', {
          amount: formatMoney(preview.estimated_amount),
          days: days(preview.requested_days),
          dayValue: formatMoney(preview.estimated_day_value),
        })
      : null
  const payDay =
    !refused && preview.requested_days != null && preview.estimated_pay_date
      ? t('payroll.settlementPreviewPayDay', { date: preview.estimated_pay_date })
      : null
  return { balance, pending, amount, payDay, problem }
}

/** A requester's own row: the priced pay and whether a final run has paid it; null before it is priced. */
export function myPayoutPayLine(
  row: Pick<Settlement, 'status' | 'amount' | 'salary_run_id'>,
  t: TFunction,
): string | null {
  if (row.status !== 'Approved' || row.amount == null) return null
  const state = row.salary_run_id ? t('payroll.settlementPaid') : t('payroll.settlementAwaitingPay')
  return `${formatMoney(row.amount)} · ${state}`
}
